<template>
  <DeleteConfirmation
    :isOpen="showDeleteConfirmation"
    :handleClose="handleCloseDeleteConfirmation"
    :handleDelete="handleDelete"
  />
  <tr class="border-b-2 border-gray-50">
    <td
      class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
    >
      {{ data.id }}
    </td>
    <td
      class="border-t-0 max-w-10 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 truncate"
    >
      <p class="max-w-[500px] truncate">{{ formattedComponentName }}</p>
    </td>
    <td
      @mouseover="handleMouseOverDesc"
      @mouseleave="handleMouseLeaveDesc"
      :class="!data.description && 'text-gray-400'"
      class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
    >
      <transition
        enter-active-class="duration-300 ease-out"
        enter-from-class="transform opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="transform opacity-0"
        mode="out-in"
      >
        <input
          v-model="description"
          v-if="showDescInput"
          type="text"
          class="block w-2/3 h-[30px] py-2 px-3 mr-2 bg-white text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        />
        <span v-else>{{
          description
            ? description
            : data.description
            ? data.description
            : "No description for this instance"
        }}</span>
      </transition>
    </td>

    <td
      class="border-t-0 flex items-center justify-end px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right"
    >
      <button
        @click="copyShortCode"
        :class="
          shortCodeCopied
            ? 'bg-green-500 active:bg-green-600'
            : 'bg-blue-500 active:bg-blue-600'
        "
        type="button"
        class="bg-blue-500 active:bg-blue-600 flex items-center text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
      >
        <IconCheck v-if="shortCodeCopied" class="w-4 h-4 m-0" />
        <IconCode v-else class="w-4 h-4 m-0" />
      </button>
      <button
        @click="handleRedirect(data)"
        class="bg-amber-400 text-white active:bg-amber-500 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        type="button"
      >
        <IconPencil class="w-4 h-4 m-0" :fill="'#ffffff'" />
      </button>
      <button
        :disabled="isCategoryComp"
        @click="handleOpenDeleteConfirmation"
        :class="
          isCategoryComp
            ? 'bg-gray-200 active:bg-gray-300'
            : 'bg-gray-400 active:bg-gray-500'
        "
        class="text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
      >
        <div class="flex items-center justify-center">
          <IconDelete v-if="!getDeleteLoading[id]" class="w-4 h-4" />
          <IconSync v-if="getDeleteLoading[id]" class="w-4 h-4 animate-spin" />
        </div>
      </button>
    </td>
  </tr>
</template>

<script>
import { mapGetters } from "vuex";
import _ from "underscore";
import * as Actions from "@/store/actions.type";
import ABILogo from "@/assets/imgs/logo.png";
import DeleteConfirmation from "@/components/Shared/Modal/DeleteConfirmation";
import IconDelete from "@/assets/icons/IconDelete";
import IconSync from "@/assets/icons/IconSync";
import IconPencil from "@/assets/icons/IconPencil";
import IconCode from "@/assets/icons/IconCode";
import IconCheck from "@/assets/icons/IconCheck";

export default {
  name: "BlogInstanceTableRow",
  components: {
    IconDelete,
    IconSync,
    IconCode,
    IconCheck,
    IconPencil,
    DeleteConfirmation
  },
  data() {
    return {
      ABILogo,
      instanceDuplicateLoading: false,
      shortCodeCopied: false,
      showComponentPreviewModal: false,
      showDeleteConfirmation: false,
      showDescInput: false
    };
  },
  props: {
    data: {
      type: [Array, Object],
      required: true
    },
    id: {
      type: Number,
      required: true
    },
    componentId: {
      type: String,
      required: true
    }
  },
  created() {
    this.debounceFn = _.debounce(() => {
      this.$store.dispatch(Actions.UPDATE_INSTANCE_DESCRIPTION, {
        region: this.getRegion,
        instanceId: this.data.id,
        description: this.description
      });
    }, 500);
  },
  mounted() {
    this.description = this.data.description;
  },
  computed: {
    description: {
      get: function () {
        return this.getInstanceDescription[this.data.id];
      },
      set: function (val) {
        this.$store.dispatch(Actions.SET_DESCRIPTION_CACHE, {
          description: val,
          instanceId: this.data.id
        });
        if (this.description !== this.data.description) {
          this.debounceFn();
        }
      }
    },
    formattedComponentName() {
      const splitName = this.data.name.split("|");
      return splitName[1];
    },
    categoryNameList() {
      if (this.categoryInfo.length) {
        return this.categoryInfo.map((item) => item.name);
      } else {
        return [];
      }
    },
    instanceData() {
      return this.getInstanceInfo.find((item) => item.id === this.data.id);
    },
    isCategoryComp() {
      if (this.componentId === this.getComponentId("abi_content_text_block")) {
        let keywords = ["faqs", "assistance", "range", "overview"];
        if (
          keywords.includes(this.formattedComponentName.split("_")[0]) &&
          this.categoryNameList.includes(
            this.formattedComponentName.split("_")[1]
          )
        ) {
          return true;
        } else {
          return false;
        }
      } else if (
        this.componentId ===
        this.getComponentId("abi_category" || "abi_category_faq")
      ) {
        if (this.categoryNameList.includes(this.formattedComponentName)) {
          return true;
        } else {
          return false;
        }
      }
      return false;
    },
    ...mapGetters([
      "categoryInfo",
      "getAllComponent",
      "getDeleteLoading",
      "getInstanceCopyLoading",
      "getInstanceDescription",
      "getInstanceInfo",
      "getRegion"
    ])
  },
  methods: {
    copyShortCode() {
      let shortCode = "";
      this.instanceData.attributes.abi_blog_components
        .split(",")
        .forEach((item) => {
          const componentId = item.split("|")[0];
          shortCode += `[${this.getComponentName(componentId)
            .toLowerCase()
            .replaceAll(" ", "_")} name=${item}]`;
        });
      navigator.clipboard.writeText(shortCode);
      this.shortCodeCopied = true;
      setTimeout(() => {
        this.shortCodeCopied = false;
      }, 3000);
    },
    getComponentId(componentName) {
      if (this.getAllComponent.find((item) => item.name === componentName)) {
        return this.getAllComponent.find((item) => item.name === componentName)
          .id;
      } else {
        return null;
      }
    },
    getComponentName(componentId) {
      if (this.getAllComponent.find((item) => item.id === componentId)) {
        return this.getAllComponent.find((item) => item.id === componentId)
          .name;
      } else {
        return null;
      }
    },
    handleDelete() {
      this.$store.dispatch(Actions.DELETE_COMPONENT_DATA, {
        region: this.getRegion,
        instanceId: this.data.id,
        index: this.id
      });
      this.showDeleteConfirmation = false;
    },
    handleOpenDeleteConfirmation() {
      this.showDeleteConfirmation = true;
    },
    handleCloseDeleteConfirmation() {
      this.showDeleteConfirmation = false;
    },
    handleRedirect(data) {
      this.$store.dispatch(Actions.CHANGE_BLOG_OPERATION, {
        operation: "edit"
      });
      this.$router.push({
        path: `/component/blog-page/edit/${data.id}`
      });
    },
    handleMouseOverDesc() {
      this.showDescInput = true;
    },
    handleMouseLeaveDesc() {
      this.showDescInput = false;
    },
    checkCopyExist() {
      let copyExist = false;
      this.getInstanceInfo.forEach((item) => {
        if (
          item.instance_name.split("|")[1] ===
          this.formattedComponentName + "_copy"
        ) {
          copyExist = true;
        }
      });
      return copyExist;
    }
  }
};
</script>
